/** If tags are found in the `state` for the given `tagName`,
 * returns a concatenation of '&tagNameTags=tag'.
 * @example
 * type TagColumnNames = 'color';
 * let state = {
 * 	tags: {
 * 		color: ['blue','orange'],
 * 	},
 * };
 *
 * let queryParam = getTagQueryParam<TagColmunNames>(state, 'color');
 * // the expected output is '&colorTags=blue&colorTags=orange'
 */
export const getTagQueryParam = <T extends string>(
  tags: Record<T, string[]>,
  tagName: T,
): string => {
  return tags[tagName][0]
    ? tags[tagName].map((tag) => `&${tagName}Tags=${tag}`).join('')
    : '';
};

/** Concatenates the query params obtained using the `getTagQueryParam` function
 * on an array of tag names.
 *
 * @example
 * type TagColumnNames = 'color' | 'brand';
 * let state = {
 * 	tags: {
 * 		color: ['blue','orange'],
 * 		brand: ['BMW'],
 * 	},
 * };
 *
 * let queryParam = getTagQueryParam<TagColmunNames>(state, ['color', 'brand']);
 * // the expected output is '&colorTags=blue&colorTags=orange&brandTags=BMW'
 */
const getTagsQueryParams = <T extends string>(
  tags: Record<T, string[]>,
  tagNames: T[],
): string => {
  let result = '';
  for (let i = 0; i < tagNames.length; i++) {
    result = result + getTagQueryParam(tags, tagNames[i]);
  }
  return result;
};

export default getTagsQueryParams;
